import { useState, useEffect, useCallback, useMemo } from 'react';
import { PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { getTaskAndSubtaskIds } from '../utils/taskUtils';
import { DND_DELAY_MOBILE, DND_MOBILE_TOLERANCE_PX, DND_SENTINEL_BEFORE_FIRST, DND_SENTINEL_AFTER_LAST } from '../utils/dndConstants';
import { renumberSequentialListAroundTask } from '../utils/sequentialTasks';
import {
  createPointerCollisionDetection,
  calculateDropPosition as calcDropPosition,
  isValidDropTarget
} from '../utils/dragDropHelpers';

function useIsTouchDevice() {
  return useMemo(
    () =>
      typeof window !== 'undefined' &&
      (navigator.maxTouchPoints > 0 || 'ontouchstart' in window),
    []
  );
}

/**
 * Hook for handling drag-and-drop operations with sequential task support
 * @param {Object} params - Configuration object
 * @param {Array} params.tasks - All tasks
 * @param {string} params.currentFileId - Current file ID
 * @param {Function} params.updateTaskOrders - Function to batch update task orders
 * @param {Function} params.fetchTasks - Function to refresh tasks on error
 * @param {string|null} params.firstRootTaskKey - _reactKey of first root task (for sentinel "drop before first")
 * @param {string|null} params.lastRootTaskKey - _reactKey of last root task (for sentinel "drop after last")
 */
export function useTaskDragDropWithSequential({
  tasks,
  currentFileId,
  updateTaskOrders,
  fetchTasks,
  firstRootTaskKey = null,
  lastRootTaskKey = null
}) {
  const [dragOverTaskId, setDragOverTaskId] = useState(null);
  const [dropPosition, setDropPosition] = useState(null); // 'before' or 'after'
  const [mouseY, setMouseY] = useState(null);

  // Pointer-based collision so drop indicator appears at list start/end when cursor is there
  const collisionDetection = useCallback(createPointerCollisionDetection(), []);

  const isTouchDevice = useIsTouchDevice();
  // Desktop: PointerSensor (5px) so mouse drag works. Touch: only TouchSensor (long-press + tolerance) so scrolling doesn't start a drag.
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 5 },
  });
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: { delay: DND_DELAY_MOBILE, tolerance: DND_MOBILE_TOLERANCE_PX },
  });
  const sensors = useSensors(
    ...(isTouchDevice ? [touchSensor] : [pointerSensor, touchSensor])
  );

  // Track pointer/touch position during drag (mouseY used for drop position calculation)
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMouseY(e.clientY);
    };
    const handleTouchMove = (e) => {
      if (e.touches && e.touches.length > 0) {
        setMouseY(e.touches[0].clientY);
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('touchmove', handleTouchMove);
    };
  }, []);

  // Handle drag over - track which task is being dragged over and drop position
  const handleDragOver = useCallback((event) => {
    const { over, active } = event;
    // Ignore if dropping on the dragged task itself (can happen with tall tasks)
    if (over && over.id !== active.id) {
      // Sentinel zones: map to first/last task so the blue line shows at list start/end
      if (over.id === DND_SENTINEL_BEFORE_FIRST && firstRootTaskKey) {
        setDragOverTaskId(firstRootTaskKey);
        setDropPosition('before');
        return;
      }
      if (over.id === DND_SENTINEL_AFTER_LAST && lastRootTaskKey) {
        setDragOverTaskId(lastRootTaskKey);
        setDropPosition('after');
        return;
      }

      // Find dragged task by clientId to get database ID
      const draggedTask = tasks.find(t => t._reactKey === active.id);
      if (draggedTask) {
        // Prevent showing drop indicator if hovering over a subtask of the dragged task
        const draggedTaskAndSubtaskIds = getTaskAndSubtaskIds(draggedTask.id, tasks); // Use database ID
        const dropTargetTask = tasks.find(t => t._reactKey === over.id);
        if (dropTargetTask && draggedTaskAndSubtaskIds.includes(dropTargetTask.id)) { // Use database ID
          // Clear drag over state if hovering over a subtask
          setDragOverTaskId(null);
          setDropPosition(null);
          return;
        }
      }

      setDragOverTaskId(over.id); // Store clientId

      // Calculate drop position using the helper function
      const position = calcDropPosition(over, active, mouseY);
      setDropPosition(position);
    } else if (over && over.id === active.id) {
      // Clear drag over state if hovering over the dragged task itself
      setDragOverTaskId(null);
      setDropPosition(null);
    }
  }, [mouseY, tasks, firstRootTaskKey, lastRootTaskKey]);

  // Handle drag end - update task order
  const handleDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    
    // Capture values before clearing state
    const currentMouseY = mouseY;
    const currentDropPosition = dropPosition;
    
    // Clear state
    setDragOverTaskId(null);
    setDropPosition(null);
    setMouseY(null);

    // If no drop target, do nothing
    if (!over || !active) {
      return;
    }

    const draggedTaskClientId = active.id; // Now stores clientId (_reactKey)
    // Resolve sentinel zones to the actual task id and position
    let dropTargetClientId = over.id;
    let calculatedPosition = currentDropPosition;
    if (over.id === DND_SENTINEL_BEFORE_FIRST && firstRootTaskKey) {
      dropTargetClientId = firstRootTaskKey;
      calculatedPosition = 'before';
    } else if (over.id === DND_SENTINEL_AFTER_LAST && lastRootTaskKey) {
      dropTargetClientId = lastRootTaskKey;
      calculatedPosition = 'after';
    }

    // Don't do anything if dropped on itself
    if (draggedTaskClientId === dropTargetClientId) {
      return;
    }

    // Find the dragged task and drop target task by clientId - only from current file
    const draggedTask = tasks.find(t => t._reactKey === draggedTaskClientId && t.file_id === currentFileId);
    const dropTargetTask = tasks.find(t => t._reactKey === dropTargetClientId && t.file_id === currentFileId);

    if (!draggedTask || !dropTargetTask) {
      return;
    }

    // Prevent dropping on a subtask of the dragged task
    if (!isValidDropTarget(draggedTask, dropTargetTask, tasks)) {
      return;
    }

    // Use resolved position (already set for sentinels, else from state or recalc)
    const resolvedPosition = calculatedPosition ?? currentDropPosition ?? calcDropPosition(over, active, currentMouseY);
    const placeBefore = resolvedPosition === 'before';

    // Get siblings of the drop target (tasks with same parent and same file)
    const dropTargetParentId = dropTargetTask.parent_task_id;
    const allSiblings = tasks.filter(t => 
      t.file_id === currentFileId && 
      t.parent_task_id === dropTargetParentId
    );
    
    // Sort siblings by order
    allSiblings.sort((a, b) => {
      const orderA = a.order !== null && a.order !== undefined ? a.order : Number.MAX_SAFE_INTEGER;
      const orderB = b.order !== null && b.order !== undefined ? b.order : Number.MAX_SAFE_INTEGER;
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      return (a.created_at || '').localeCompare(b.created_at || '');
    });
    
    // Get siblings without the dragged task
    const siblingsWithoutDragged = allSiblings.filter(t => t._reactKey !== draggedTaskClientId); // Use clientId
    
    // Find the index of the drop target in siblings without dragged
    const dropTargetIndex = siblingsWithoutDragged.findIndex(t => t._reactKey === dropTargetClientId); // Use clientId
    
    if (dropTargetIndex === -1) {
      return;
    }

    // Calculate new order based on position and collect all order changes
    const taskOrders = [];
    let newOrder;
    
    if (placeBefore) {
      // Insert before drop target
      const dropTargetOrder = dropTargetTask.order !== null && dropTargetTask.order !== undefined 
        ? dropTargetTask.order 
        : 0;
      newOrder = dropTargetOrder;
      
      // Collect order updates for subsequent tasks (including drop target and all after it)
      for (let i = dropTargetIndex; i < siblingsWithoutDragged.length; i++) {
        const sibling = siblingsWithoutDragged[i];
        if (sibling.order !== null && sibling.order !== undefined && sibling.order >= dropTargetOrder) {
          taskOrders.push({ id: sibling.id, order: sibling.order + 1 });
        }
      }
    } else {
      // Insert after drop target
      const nextSibling = siblingsWithoutDragged[dropTargetIndex + 1];
      const dropTargetOrder = dropTargetTask.order !== null && dropTargetTask.order !== undefined 
        ? dropTargetTask.order 
        : 0;
      
      if (nextSibling && nextSibling.order !== null && nextSibling.order !== undefined) {
        const nextOrder = nextSibling.order;
        
        if (nextOrder - dropTargetOrder <= 1) {
          // Need to shift all subsequent tasks
          for (let i = dropTargetIndex + 1; i < siblingsWithoutDragged.length; i++) {
            const sibling = siblingsWithoutDragged[i];
            if (sibling.order !== null && sibling.order !== undefined) {
              taskOrders.push({ id: sibling.id, order: sibling.order + 1 });
            }
          }
          newOrder = dropTargetOrder + 1;
        } else {
          // There's space between drop target and next sibling
          newOrder = dropTargetOrder + 1;
        }
      } else {
        // Dropping at the end - find max order and add 1
        const maxOrder = siblingsWithoutDragged.length > 0
          ? Math.max(...siblingsWithoutDragged.map(t => t.order !== null && t.order !== undefined ? t.order : 0))
          : 0;
        newOrder = maxOrder + 1;
      }
    }

    // Add the dragged task's order and parent_task_id (if changed) to the batch
    const draggedTaskOrderUpdate = { id: draggedTask.id, order: newOrder }; // Use database ID
    if (draggedTask.parent_task_id !== dropTargetParentId) {
      draggedTaskOrderUpdate.parent_task_id = dropTargetParentId;
    }
    taskOrders.push(draggedTaskOrderUpdate);

    // Build task list as it will be after the drop (for renumbering)
    const orderById = new Map(taskOrders.map(to => [to.id, to]));
    const tasksAfterDrop = tasks.map(t => {
      if (t.id === draggedTask.id) {
        return {
          ...t,
          order: newOrder,
          parent_task_id: dropTargetParentId
        };
      }
      const o = orderById.get(t.id);
      if (o && o.order !== undefined) {
        return { ...t, order: o.order };
      }
      return t;
    });
    const draggedTaskAfterDrop = tasksAfterDrop.find(t => t.id === draggedTask.id);

    const previousOrder = draggedTask.order != null ? draggedTask.order : Number.MAX_SAFE_INTEGER;
    const movedUp = newOrder < previousOrder;

    // Renumber sequential list around the dropped task and merge text updates into batch
    const renumberUpdates = renumberSequentialListAroundTask(draggedTaskAfterDrop, tasksAfterDrop, { movedUp });
    const taskOrdersMap = new Map(taskOrders.map(to => [to.id, to]));
    renumberUpdates.forEach(update => {
      const existing = taskOrdersMap.get(update.id);
      if (existing) {
        existing.text = update.text;
      } else {
        const task = tasksAfterDrop.find(t => t.id === update.id) || tasks.find(t => t.id === update.id);
        if (task) {
          taskOrdersMap.set(update.id, { id: update.id, order: task.order, text: update.text });
        }
      }
    });
    const finalTaskOrders = Array.from(taskOrdersMap.values());

    // Update all tasks atomically using batch_orders
    try {
      await updateTaskOrders(finalTaskOrders, true, { origin: 'drag' });
    } catch (error) {
      console.error('Error updating task orders:', error);
      // On error, refresh tasks to get correct state
      if (fetchTasks) {
        await fetchTasks();
      }
    }
  }, [tasks, currentFileId, updateTaskOrders, fetchTasks, dropPosition, mouseY, firstRootTaskKey, lastRootTaskKey]);

  // Handle drag cancel - clear drag over state
  const handleDragCancel = useCallback(() => {
    setDragOverTaskId(null);
    setDropPosition(null);
    setMouseY(null);
  }, []);

  return {
    sensors,
    collisionDetection,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
    dragOverTaskId,
    dropPosition
  };
}
